/**
 * ThemeParks.wiki API Service
 * 
 * This service interacts with the ThemeParks.wiki API to fetch
 * Disney World park data including attractions, restaurants, shows, etc.
 * 
 * API Documentation: https://api.themeparks.wiki/docs/v1/
 */

const API_BASE_URL = 'https://api.themeparks.wiki/v1'

// Walt Disney World Resort ID
export const WALT_DISNEY_WORLD_ID = 'e957da41-3552-4cf6-b636-5babc5cbc4e5'

// Disney World Park IDs
export const DISNEY_PARKS = {
  MAGIC_KINGDOM: '75ea578a-adc8-4116-a54d-dccb60765ef9',
  EPCOT: '47f90d2c-e191-4239-a466-5892ef59a88b',
  HOLLYWOOD_STUDIOS: '288747d1-8b4f-4a64-867e-ea7c9b27bad8',
  ANIMAL_KINGDOM: '1c84a229-8862-4648-9c71-378ddd2c7693',
  TYPHOON_LAGOON: 'b070cbc5-feaa-4b87-a8c1-f94cca037a18',
  BLIZZARD_BEACH: 'ead53ea5-22e5-4095-9a83-8c29300d7c63',
} as const

export type EntityType = 
  | 'DESTINATION'
  | 'PARK'
  | 'LAND'
  | 'AREA'
  | 'ATTRACTION'
  | 'RESTAURANT'
  | 'SHOW'
  | 'ENTERTAINMENT'

export interface Location {
  latitude: number
  longitude: number
  pointsOfInterest?: any[]
}

export interface ThemeParksEntity {
  id: string
  name: string
  slug?: string | null
  entityType: EntityType
  parentId?: string
  destinationId?: string
  externalId?: string
  timezone?: string
  location?: Location
  attractionType?: string  // For attractions: RIDE, SHOW, etc.
  cuisines?: string[]      // For restaurants: array of cuisine types
  description?: string | null  // Description (usually null from API)
  images?: string[] | null     // Images (usually null from API)
}

export interface ThemeParksDestination {
  id: string
  name: string
  slug: string
  parks: Array<{
    id: string
    name: string
  }>
}

export interface ThemeParksChildren {
  id: string
  name: string
  entityType: EntityType
  timezone: string
  children: ThemeParksEntity[]
}

export interface QueueInfo {
  STANDBY?: {
    waitTime: number | null
  }
  SINGLE_RIDER?: {
    waitTime: number | null
  }
  RETURN_TIME?: {
    state: string
    returnStart: string | null
    returnEnd: string | null
  }
}

export interface WaitTimeForecast {
  time: string
  waitTime: number
  percentage: number
}

export interface ShowTime {
  type: string
  startTime: string
  endTime: string
}

export interface LiveDataEntity {
  id: string
  name: string
  entityType: EntityType
  parkId: string
  externalId: string
  queue?: QueueInfo
  status: 'OPERATING' | 'CLOSED' | 'DOWN' | 'REFURBISHMENT'
  forecast?: WaitTimeForecast[]
  showtimes?: ShowTime[]
  lastUpdated?: string
}

export interface LiveData {
  id: string
  name: string
  entityType: EntityType
  timezone: string
  liveData: LiveDataEntity[]
}

export interface ScheduleEntry {
  date: string
  type: 'OPERATING' | 'TICKETED_EVENT' | 'INFO' | 'CLOSED'
  openingTime?: string
  closingTime?: string
  description?: string
  purchases?: Array<{
    id: string
    name: string
    type: string
    price?: {
      amount: number
      currency: string
      formatted: string
    }
    available: boolean
  }>
}

export interface Schedule {
  id: string
  name: string
  entityType: EntityType
  timezone: string
  schedule: ScheduleEntry[]
}

/**
 * Fetch all destinations from ThemeParks.wiki API
 */
export async function getDestinations(): Promise<ThemeParksDestination[]> {
  const response = await fetch(`${API_BASE_URL}/destinations`)
  if (!response.ok) {
    throw new Error(`Failed to fetch destinations: ${response.statusText}`)
  }
  const data = await response.json()
  return data.destinations
}

/**
 * Fetch a specific entity by ID
 */
export async function getEntity(entityId: string): Promise<ThemeParksEntity> {
  const response = await fetch(`${API_BASE_URL}/entity/${entityId}`)
  if (!response.ok) {
    throw new Error(`Failed to fetch entity ${entityId}: ${response.statusText}`)
  }
  return await response.json()
}

/**
 * Fetch all children of an entity (e.g., all attractions in a park)
 */
export async function getEntityChildren(entityId: string): Promise<ThemeParksChildren> {
  const response = await fetch(`${API_BASE_URL}/entity/${entityId}/children`)
  if (!response.ok) {
    throw new Error(`Failed to fetch children for entity ${entityId}: ${response.statusText}`)
  }
  return await response.json()
}

/**
 * Fetch Walt Disney World Resort data
 */
export async function getWaltDisneyWorld(): Promise<ThemeParksEntity> {
  return getEntity(WALT_DISNEY_WORLD_ID)
}

/**
 * Fetch all Disney World parks
 */
export async function getDisneyWorldParks(): Promise<ThemeParksEntity[]> {
  const parks = await Promise.all(
    Object.values(DISNEY_PARKS).map(parkId => getEntity(parkId))
  )
  return parks
}

/**
 * Fetch all entities (attractions, shows, restaurants) for a specific park
 */
export async function getParkEntities(parkId: string): Promise<ThemeParksEntity[]> {
  const data = await getEntityChildren(parkId)
  return data.children
}

/**
 * Fetch all entities for all Disney World parks
 */
export async function getAllDisneyWorldEntities(): Promise<Map<string, ThemeParksEntity[]>> {
  const entitiesMap = new Map<string, ThemeParksEntity[]>()
  
  for (const [parkName, parkId] of Object.entries(DISNEY_PARKS)) {
    const entities = await getParkEntities(parkId)
    entitiesMap.set(parkId, entities)
  }
  
  return entitiesMap
}

/**
 * Fetch live data for an entity (wait times, status, forecasts)
 */
export async function getLiveData(entityId: string): Promise<LiveData> {
  const response = await fetch(`${API_BASE_URL}/entity/${entityId}/live`)
  if (!response.ok) {
    throw new Error(`Failed to fetch live data for entity ${entityId}: ${response.statusText}`)
  }
  return await response.json()
}

/**
 * Fetch schedule for an entity (operating hours, special events)
 */
export async function getSchedule(entityId: string): Promise<Schedule> {
  const response = await fetch(`${API_BASE_URL}/entity/${entityId}/schedule`)
  if (!response.ok) {
    throw new Error(`Failed to fetch schedule for entity ${entityId}: ${response.statusText}`)
  }
  return await response.json()
}

/**
 * Fetch live data for all Disney World parks
 */
export async function getAllDisneyWorldLiveData(): Promise<Map<string, LiveData>> {
  const liveDataMap = new Map<string, LiveData>()

  for (const [parkName, parkId] of Object.entries(DISNEY_PARKS)) {
    try {
      const liveData = await getLiveData(parkId)
      liveDataMap.set(parkId, liveData)
    } catch (error) {
      console.error(`Failed to fetch live data for ${parkName}:`, error)
    }
  }

  return liveDataMap
}

/**
 * Map ThemeParks.wiki entity type to our database entity type
 */
export function mapEntityType(themeParksType: EntityType): string {
  const mapping: Record<EntityType, string> = {
    DESTINATION: 'PARK', // Destinations map to our top-level parks
    PARK: 'PARK',
    LAND: 'LAND',
    AREA: 'AREA',
    ATTRACTION: 'ATTRACTION',
    RESTAURANT: 'RESTAURANT',
    SHOW: 'SHOW',
    ENTERTAINMENT: 'ENTERTAINMENT',
  }

  return mapping[themeParksType] || 'ATTRACTION'
}

